import * as React from "react"
import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { batch } from "@preact/signals-react"
import {
	Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel,
	IconButton, MenuItem, Paper, Popover, Stack, TextField, Tooltip, Typography,
	CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from "@mui/material"
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'

import { BACKEND_APIS } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import { vtPageProps } from "../pageHeader"
import PageWrapper from "Pages/@PageWrapper"

import { vtApi as api } from "../api"
import {
	retailers, sections, selectedRetailerId, selectedSectionId, selectedDate,
	sessionData, isLoading, errorMsg, focusedBannerIdx, clearReviewSignals,
	editingBannerIds, editDrafts, editAllMode, isSavingEdit,
	brandCatalogue, lightboxSrc,
	newsletterAssets, selectedAssetPath, isLoadingAssets,
	onlyNeedsQa,
} from "./@signals"


// Apply the human's "Map this raw brand to canonical X" resolution to the
// draft (parent or sub). Mutates in place: removes the raw string from
// ai_review.unmatched_brands, appends the canonical to brands (idempotent),
// and re-renders. The persistence to DIPI_BRAND_MAPPING already happened
// in the popover before we got here.
export function _resolveUnmatched(item, raw, canonical, onChange) {
	if (!item || !canonical?.id_brand) return
	const ai = item.ai_review || (item.ai_review = {})
	ai.unmatched_brands = (ai.unmatched_brands || []).filter(s => s !== raw)
	const brands = item.brands || (item.brands = [])
	if (!brands.some(b => b?.id_brand === canonical.id_brand)) {
		brands.push({ id_brand: canonical.id_brand, name: canonical.name })
	}
	// Once every raw string has been mapped, the AI flag is stale — clear
	// it so the chip / "needs QA" badge drops out of the UI immediately
	// and the eventual validate patch persists a clean state.
	if (ai.unmatched_brands.length === 0) {
		ai.needs_qa = false
		ai.qa_reason = ''
	}
	if (typeof onChange === 'function') onChange()
}


// A list of labels the way the back is supposed to send it. It normally
// already is one, but this page has NO error boundary: a single leaf whose
// `eyecatch` arrived as the STRING `'["petit prix"]'` (869egr4qy — a JSON
// column dumped twice, published verbatim) reached `eyecatch.map` and blanked
// the whole page, header included. Coerce at the edge instead: a JSON array in
// text form is parsed, a bare label becomes a one-item list, anything else is
// empty. MUI's `Autocomplete multiple` needs the same treatment — it throws on
// a string `value` too.
export function asStringList(v) {
	if (Array.isArray(v)) return v
	if (typeof v === 'string') {
		const s = v.trim()
		if (!s) return []
		if (s.startsWith('[')) {
			try {
				const parsed = JSON.parse(s)
				return Array.isArray(parsed) ? parsed : [parsed]
			} catch {
				return []
			}
		}
		return [s]
	}
	return []
}


// True if the parent itself needs QA OR any of its sub-items does. Used
// by the "Only needs QA" filter and the EditAllBar counter.
function groupNeedsQa(g) {
	if (!g) return false
	if (g.parent?.ai_review?.needs_qa) return true
	return (g.subs || []).some(s => s?.ai_review?.needs_qa)
}


// Build the by-section query string used by both GET and PATCH. asset_path
// is required for newsletter sections — the backend rejects calls without
// it for kind=newsletter.
function _bySectionQuery(idSection, date, assetPath) {
	const qs = new URLSearchParams({ id_section: String(idSection), date })
	if (assetPath) qs.set('asset_path', assetPath)
	return qs.toString()
}


// Mirrors the extension's SUBTYPES_BY_KIND so the dropdown offers the same
// values the tagger had originally.
const SUBTYPES_BY_KIND = {
	banner:           ["promotion", "categoria", "marca", "other"],
	interstitial:     ["promotion", "categoria", "marca", "other"],
	slider:           ["promotion", "categoria", "marca", "other"],
	"product-slider": ["top_sales", "offers", "news", "recommended", "brand", "other"],
}
const CATEGORIES = ["fragrances", "makeup", "cosmetic", "other"]
// Puig's catalogue is the source of truth for brands, so this one call is
// cross-origin on purpose. It points at the k8s ingress (the Cloud Run
// deployment behind skumapper.puig.dataseekers.com is being retired); the
// back answers it without credentials and echoes the Origin back in CORS.
const BRAND_CATALOGUE_URL = "https://k8.puig.dataseekers.com/scrape-matcher-back/api/vt/brands"


// Visibility colour ramp matches the scraper-side annotation: green for high
// visibility, fading to red for low. Returns a hex string.
function visibilityColor(score) {
	if (score >= 80) return "#00c853"
	if (score >= 50) return "#ffa000"
	if (score >= 30) return "#ef6c00"
	return "#d32f2f"
}

// Items the AI flags for human review (or that we flag because brand
// normalisation produced unmatched strings) get highlighted with this
// colour. One source of truth so the badge, the border and the dim
// state stay coherent.
const NEEDS_QA_COLOR = "#dc2626"


export default function ReviewPage() {
	const { showToast } = useAppToast()
	const location = useLocation()
	// Deep-link parameters consumed once on mount. The Pipeline dashboard
	// (and any other page) can link to /banners-review with one or more of
	// ?id_retailer=&id_section=&date=&asset_path= to pre-select the row.
	// Date and retailer are written straight to the signals; the section and
	// the newsletter asset can only be applied once their list has loaded, so
	// they wait here until `loadSections` / `loadAssets` see them.
	const pendingDeepLink = useRef(null)
	// One sequence number per loader. Every response checks it before writing,
	// so a slow reply for a filter the user has already moved past is dropped
	// instead of overwriting the current list (the sections call takes ~150ms
	// and a session 2-4s, long enough for two of them to be in flight).
	const retailersReq = useRef(0)
	const sectionsReq = useRef(0)
	const sessionReq = useRef(0)
	const assetsReq = useRef(0)

	// Date-first flow: only retailers/sections that already have data for the picked date.
	// These loaders LOAD — they never clear a sibling filter. Cascading resets
	// live in the onChange handlers, where they run synchronously: doing them
	// after an `await` is what used to wipe freshly-loaded data (a second
	// effect pass landing on top of the first one's response).
	const loadRetailers = async (date) => {
		const seq = ++retailersReq.current
		if (!date) { retailers.value = []; return }
		const r = await api.get(`${BACKEND_APIS.VT}/admin/available/retailers?date=${date}`)
		if (seq !== retailersReq.current) return
		if (!r.ok) return showToast.error('Failed to load retailers: ' + r.error)
		retailers.value = r.data?.items || []
	}
	const loadSections = async (id_retailer, date) => {
		const seq = ++sectionsReq.current
		if (!id_retailer || !date) { sections.value = []; return }
		const r = await api.get(`${BACKEND_APIS.VT}/admin/available/sections?id_retailer=${id_retailer}&date=${date}`)
		if (seq !== sectionsReq.current) return
		if (!r.ok) return showToast.error('Failed to load sections: ' + r.error)
		sections.value = r.data?.items || []
		// A deep-linked section can only be selected now that its list is here.
		// Discard it if the retailer/date pair doesn't actually hold it.
		const wanted = pendingDeepLink.current?.id_section
		if (wanted) {
			pendingDeepLink.current.id_section = null
			if (sections.value.some(s => s.id_section === wanted)) selectedSectionId.value = wanted
		}
	}
	const loadSession = async () => {
		const seq = ++sessionReq.current
		sessionData.value = null
		errorMsg.value = null
		focusedBannerIdx.value = null
		// Every early return below also drops the spinner: a superseded response
		// leaves it to whoever replaced it, so the newest run owns the flag even
		// when it has nothing to fetch — otherwise "Loading session…" sticks.
		if (!selectedSectionId.value || !selectedDate.value) { isLoading.value = false; return }

		const section = sections.value.find(s => s.id_section === selectedSectionId.value)
		const isNewsletter = section?.kind === 'newsletter'

		// Newsletter: ask the user to pick an asset before pulling a session.
		if (isNewsletter && !selectedAssetPath.value) {
			errorMsg.value = null
			isLoading.value = false
			return
		}

		isLoading.value = true
		const r = await api.get(
			`${BACKEND_APIS.VT}/admin/sessions/by-section?` +
			_bySectionQuery(selectedSectionId.value, selectedDate.value, selectedAssetPath.value),
		)
		if (seq !== sessionReq.current) return   // the user moved on; that 2-4s payload is stale
		isLoading.value = false
		if (!r.ok) {
			if (r.status === 404) {
				errorMsg.value = r.error || 'No session for this section/date.'
			} else {
				showToast.error('Failed to load session: ' + r.error)
			}
			return
		}
		sessionData.value = r.data
	}

	const loadAssets = async () => {
		const seq = ++assetsReq.current
		newsletterAssets.value = []
		if (!selectedSectionId.value || !selectedDate.value) { isLoadingAssets.value = false; return }
		const section = sections.value.find(s => s.id_section === selectedSectionId.value)
		if (section?.kind !== 'newsletter') { isLoadingAssets.value = false; return }
		isLoadingAssets.value = true
		const r = await api.get(
			`${BACKEND_APIS.VT}/admin/sessions/by-section/assets?id_section=${selectedSectionId.value}&date=${selectedDate.value}`
		)
		if (seq !== assetsReq.current) return
		isLoadingAssets.value = false
		if (!r.ok) {
			showToast.error('Failed to load newsletter assets: ' + r.error)
			return
		}
		newsletterAssets.value = r.data?.items || []
		// Apply deep-linked asset_path if it's still pending and the asset
		// actually exists in the loaded list. Discard otherwise.
		if (pendingDeepLink.current?.asset_path) {
			const wanted = pendingDeepLink.current.asset_path
			if (newsletterAssets.value.some(it => it.asset_path === wanted)) {
				selectedAssetPath.value = wanted
			}
			pendingDeepLink.current.asset_path = null
		}
	}

	useEffect(() => {
		// Parse deep-link query params on mount and seed date + retailer, so the
		// load effects below walk the rest of the chain. The section and the
		// newsletter asset stay pending until their list is loaded.
		const params = new URLSearchParams(location.search)
		const r = params.get('id_retailer')
		const s = params.get('id_section')
		const d = params.get('date')
		const a = params.get('asset_path')
		if (r || s || d || a) {
			pendingDeepLink.current = {
				id_section: s ? Number(s) : null,
				asset_path: a || null,
			}
			batch(() => {
				if (d) selectedDate.value = d
				if (r) selectedRetailerId.value = Number(r)
			})
		}
		// Retailers load from the date effect below (date-first flow).
		return clearReviewSignals
	}, [])
	// The three effects below only LOAD. Seeding the signals on mount changes
	// their deps, so React runs them a second time — which is why none of them
	// may clear a filter: the reset used to land after its `await` and wipe the
	// list the first pass had just loaded, leaving a greyed-out Section select
	// next to a 200 response holding every section. Cascading resets belong to
	// the onChange handlers, where they run before any request goes out.
	useEffect(() => {
		loadRetailers(selectedDate.value)
	}, [selectedDate.value])
	useEffect(() => {
		loadSections(selectedRetailerId.value, selectedDate.value)
	}, [selectedRetailerId.value, selectedDate.value])
	useEffect(() => {
		loadAssets()
	}, [selectedSectionId.value, selectedDate.value])
	// One effect owns the session, keyed on everything it reads. Splitting it
	// per-signal fired it twice for a single change (section + its asset reset
	// land in the same batch) — twice a 2-4MB payload.
	useEffect(() => {
		loadSession()
	}, [selectedSectionId.value, selectedDate.value, selectedAssetPath.value])

	// Picking a filter invalidates everything below it. Each handler clears its
	// own subtree in one `batch` so the load effects see the final state and
	// fire once, and a half-typed date (the native picker emits '') can't leave
	// a retailer selected against no date.
	const pickDate = (date) => batch(() => {
		selectedDate.value = date
		selectedRetailerId.value = null
		sections.value = []
		selectedSectionId.value = null
		selectedAssetPath.value = null
		sessionData.value = null
	})
	const pickRetailer = (id_retailer) => batch(() => {
		selectedRetailerId.value = id_retailer
		selectedSectionId.value = null
		selectedAssetPath.value = null
		sessionData.value = null
	})
	const pickSection = (id_section) => batch(() => {
		selectedSectionId.value = id_section
		selectedAssetPath.value = null
		sessionData.value = null
	})

	return <PageWrapper
		{...vtPageProps('Banners Review', 'Inspect a tagged session — screenshot, bbox overlays and visibility scores')}
	>
		<Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
			<TextField size="small" label="Date" type="date" sx={{ width: 180 }}
				value={selectedDate.value}
				onChange={e => { pickDate(e.target.value) }}
				InputLabelProps={{ shrink: true }}
			/>
			<TextField select size="small" label="Retailer" sx={{ minWidth: 220 }}
				value={selectedRetailerId.value || ''}
				onChange={e => { pickRetailer(Number(e.target.value) || null) }}
				disabled={!selectedDate.value || retailers.value.length === 0}
				helperText={selectedDate.value && retailers.value.length === 0 ? 'No retailers with data for this date' : undefined}
			>
				{retailers.value.filter(r => r.active).map(r => (
					<MenuItem key={r.id_retailer} value={r.id_retailer}>{r.slug}</MenuItem>
				))}
			</TextField>
			<TextField select size="small" label="Section" sx={{ minWidth: 220 }}
				value={selectedSectionId.value || ''}
				onChange={e => { pickSection(Number(e.target.value) || null) }}
				disabled={!selectedRetailerId.value || sections.value.length === 0}
				helperText={selectedRetailerId.value && sections.value.length === 0 ? 'No sections with data for this date' : undefined}
			>
				{sections.value.filter(s => s.active).map(s => (
					<MenuItem key={s.id_section} value={s.id_section}>{s.slug}</MenuItem>
				))}
			</TextField>
		</Stack>

		<NewsletterAssetPicker />

		{isLoading.value && (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
				<CircularProgress size={22} />
				<Typography variant="body2">Loading session…</Typography>
			</Box>
		)}

		{!isLoading.value && errorMsg.value && (
			<Alert severity="info">{errorMsg.value}</Alert>
		)}

		{!isLoading.value && !errorMsg.value && !sessionData.value && (
			<Alert severity="info">Pick a date, then a retailer and section to load a session.</Alert>
		)}

		{!isLoading.value && sessionData.value && (
			<>
				<EditAllBar showToast={showToast} />
				<ReviewLayout data={sessionData.value} />
			</>
		)}

		<Lightbox />
	</PageWrapper>
}


// Newsletter chooser: shown only when the selected section is kind=newsletter.
// Each row in the section's GCS folder for the selected date becomes a chip;
// the admin clicks one to load that jpg's tagged session.
function NewsletterAssetPicker() {
	const section = sections.value.find(s => s.id_section === selectedSectionId.value)
	if (!section || section.kind !== 'newsletter') return null
	if (isLoadingAssets.value) {
		return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
			<CircularProgress size={18} />
			<Typography variant="body2" color="text.secondary">Loading newsletter assets…</Typography>
		</Box>
	}
	const items = newsletterAssets.value
	if (items.length === 0) {
		return <Alert severity="info" sx={{ mb: 2 }}>
			No newsletter assets for {selectedDate.value} in this section.
		</Alert>
	}
	return <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
		<Typography variant="subtitle2" sx={{ mb: 1 }}>
			Newsletter — pick a jpg ({items.length})
		</Typography>
		<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
			{items.map(it => {
				const isSelected = selectedAssetPath.value === it.asset_path
				const isDone = it.status === 'done'
				return <Chip
					key={it.asset_path}
					label={it.asset_path}
					onClick={() => { selectedAssetPath.value = it.asset_path }}
					color={isSelected ? 'primary' : (isDone ? 'success' : 'default')}
					variant={isSelected ? 'filled' : 'outlined'}
					sx={{ maxWidth: 540, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
				/>
			})}
		</Stack>
	</Paper>
}


// Bulk-edit toolbar shown above the review layout. Lets the QA flip every
// banner into edit mode at once, then save them all in a single PATCH.
function EditAllBar({ showToast }) {
	const banners = sessionData.value?.banners || []
	const allMode = editAllMode.value
	const draftCount = editDrafts.value.size
	if (banners.length === 0) return null
	// Pre-AI sessions are view-only: show why instead of the edit controls.
	if (!isSessionEditable()) {
		const stage = sessionData.value?.pipeline_stage || 'pending'
		return <Alert severity="info" icon={<VisibilityIcon fontSize="inherit" />} sx={{ mb: 2 }}>
			<b>View-only.</b> This section hasn't finished AI review yet
			(stage: <code>{stage}</code>). You can inspect everything, but
			editing unlocks once the AI tagging pass completes.
		</Alert>
	}
	return <Box sx={{
		mb: 2, p: 1.5,
		display: 'flex', alignItems: 'center', gap: 1.5,
		bgcolor: allMode ? '#fff7e6' : '#f8f9fb',
		border: `1px solid ${allMode ? '#ffd591' : '#e0e6ee'}`,
		borderRadius: 1,
	}}>
		{!allMode ? (
			<>
				<Box sx={{ flex: 1, fontSize: 13, color: 'text.secondary' }}>
					Click <b>Edit all</b> to put every banner into edit mode and save in one go.
				</Box>
				<Button size="small" variant="outlined"
					startIcon={<EditIcon fontSize="small" />}
					onClick={startEditAll}
				>Edit all ({banners.length})</Button>
			</>
		) : (
			<>
				<Box sx={{ flex: 1, fontSize: 13, color: '#874d00' }}>
					<b>Edit-all mode.</b> {draftCount} banner{draftCount === 1 ? '' : 's'} pending save.
					Per-card buttons are hidden — use Save all when ready.
				</Box>
				<Button size="small" variant="contained" color="primary"
					startIcon={isSavingEdit.value
						? <CircularProgress size={14} color="inherit" />
						: <SaveIcon fontSize="small" />}
					disabled={isSavingEdit.value}
					onClick={() => saveEditAll({ showToast })}
				>{isSavingEdit.value ? 'Saving…' : `Save all (${draftCount})`}</Button>
				<Button size="small" variant="outlined" color="inherit"
					startIcon={<CloseIcon fontSize="small" />}
					disabled={isSavingEdit.value}
					onClick={cancelEditAll}
				>Cancel all</Button>
			</>
		)}
	</Box>
}


// Full-screen image viewer. Accepts either a single image string or an
// object { sources: [...], index } for multi-image groups (sliders /
// brand-logos / etc) — in that case the user can paginate with the on-
// screen arrows or the keyboard arrows.
function Lightbox() {
	const src = lightboxSrc.value
	const isMulti = src && typeof src === 'object' && Array.isArray(src.sources)
	const sources = isMulti ? src.sources : (src ? [src] : [])
	const index = isMulti ? (src.index || 0) : 0

	const close = () => { lightboxSrc.value = null }
	const goTo = (newIdx) => {
		if (!isMulti) return
		const n = sources.length
		const wrapped = ((newIdx % n) + n) % n
		lightboxSrc.value = { sources, index: wrapped }
	}
	const next = () => goTo(index + 1)
	const prev = () => goTo(index - 1)

	useEffect(() => {
		const onKey = (e) => {
			if (!lightboxSrc.value) return
			if (e.key === 'Escape') close()
			else if (e.key === 'ArrowRight') next()
			else if (e.key === 'ArrowLeft') prev()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [src])

	if (sources.length === 0) return null
	const currentSrc = sources[index]
	return <Box
		onClick={close}
		sx={{
			position: 'fixed', inset: 0, zIndex: 1500,
			display: 'flex', alignItems: 'center', justifyContent: 'center',
			bgcolor: 'rgba(12, 26, 62, 0.85)',
			cursor: 'zoom-out',
			p: 4,
		}}
	>
		<Box component="img" src={currentSrc} alt=""
			sx={{
				maxWidth: '95vw', maxHeight: '95vh',
				objectFit: 'contain',
				boxShadow: '0 12px 60px rgba(0,0,0,0.5)',
				borderRadius: 1,
				background: '#fff',
			}}
		/>
		{isMulti && sources.length > 1 && (
			<>
				<Box onClick={(e) => { e.stopPropagation(); prev() }}
					sx={{
						position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)',
						width: 56, height: 56, borderRadius: '50%',
						bgcolor: 'rgba(255,255,255,0.85)', color: '#0c1a3e',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						fontSize: 28, fontWeight: 700, cursor: 'pointer',
						boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
						userSelect: 'none',
						'&:hover': { bgcolor: '#fff' },
					}}
				>‹</Box>
				<Box onClick={(e) => { e.stopPropagation(); next() }}
					sx={{
						position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)',
						width: 56, height: 56, borderRadius: '50%',
						bgcolor: 'rgba(255,255,255,0.85)', color: '#0c1a3e',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						fontSize: 28, fontWeight: 700, cursor: 'pointer',
						boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
						userSelect: 'none',
						'&:hover': { bgcolor: '#fff' },
					}}
				>›</Box>
				<Box sx={{
					position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
					bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
					padding: '6px 14px', borderRadius: 999,
					fontSize: 13, fontWeight: 600,
					pointerEvents: 'none',
				}}>
					{index + 1} / {sources.length}
				</Box>
			</>
		)}
		<Box sx={{
			position: 'absolute', top: 16, right: 20,
			color: '#fff', fontSize: 12, opacity: 0.7,
			pointerEvents: 'none',
		}}>
			{isMulti && sources.length > 1 ? '← → to navigate · ESC to close' : 'Click anywhere or ESC to close'}
		</Box>
	</Box>
}


// Helper to open a lightbox with all crops of a multi-item group.
function openGroupLightbox(group, startSubIdx = 0) {
	const subs = group.subs || []
	if (subs.length > 0) {
		const sources = subs.map(s => s.crop).filter(Boolean)
		if (sources.length === 0) return
		// Try to start at the requested sub-item if it has a crop.
		const wanted = subs[startSubIdx]?.crop
		const idx = wanted ? Math.max(0, sources.indexOf(wanted)) : 0
		lightboxSrc.value = { sources, index: idx }
		return
	}
	if (group.parent?.crop) lightboxSrc.value = group.parent.crop
}


function ReviewLayout({ data }) {
	const screenshotPaneRef = useRef(null)
	const cardsPaneRef = useRef(null)
	const groupRefs = useRef({})
	const overlayRefs = useRef({})
	const subRefs = useRef({})    // subRefs[gIdx][subIdx] = SubItemRow root

	// Build a hierarchy: each banner becomes a "group" with optional sub-items.
	// Single-image kinds (banner / interstitial): the group has just the parent.
	// Multi-item kinds (slider / product-slider / brand-logos / product-list):
	// the group keeps the parent's metadata and its subs as children — they
	// render together visually so the QA sees the slider as one block, not as
	// N independent rows.
	const allGroups = React.useMemo(() => buildGroups(data.banners || []), [data])
	const groups = React.useMemo(
		() => onlyNeedsQa.value ? allGroups.filter(groupNeedsQa) : allGroups,
		[allGroups, onlyNeedsQa.value],
	)
	const needsQaCount = React.useMemo(() => allGroups.filter(groupNeedsQa).length, [allGroups])

	// Focus a specific sub-item's overlay if it exists (per-item kinds —
	// product-list / legacy brand-logos with logos[]); otherwise fall back
	// to the group's parent-level overlay. Pass subIdx=null when the click
	// originated on the group as a whole. `origin` says which pane the
	// click came from so we DON'T re-scroll that pane (the user is already
	// looking where they want); we only scroll the other one.
	const focusSub = (gIdx, subIdx, origin) => {
		focusedBannerIdx.value = gIdx
		const overlays = overlayRefs.current[gIdx] || {}
		const overlay = (subIdx != null && overlays[subIdx]) || overlays.__group__
		// Cards pane: prefer the specific SubItemRow when a subIdx is
		// provided and we have a ref for it (per-item kinds — product-list
		// fragments etc.). Fallback to the group header for kinds where
		// children share one parent rectangle.
		const subSlot = subRefs.current[gIdx] || {}
		const card = (subIdx != null && subSlot[subIdx]) || groupRefs.current[gIdx]
		if (origin !== 'screenshot' && overlay && screenshotPaneRef.current) {
			screenshotPaneRef.current.scrollTo({ top: overlay.offsetTop - 60, behavior: 'smooth' })
		}
		if (origin !== 'cards' && card && cardsPaneRef.current) {
			cardsPaneRef.current.scrollTo({ top: card.offsetTop - 12, behavior: 'smooth' })
		}
	}
	const focusGroup = (gIdx, origin) => focusSub(gIdx, null, origin)

	// Lock the screenshot pane to the captured viewport width so the image
	// always renders at 1:1 of the bbox coordinate system. Anything narrower
	// would either cause the browser to scale the <img> down (and the
	// overlays — positioned in absolute pixels — to detach from the banners)
	// or trigger horizontal scroll inside the pane.
	const imgWidth = data.viewport?.width || 393
	const paneWidth = imgWidth + 24   // viewport + a little padding for the scroll-y gutter

	return <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 240px)', minHeight: 500 }}>
		{/* LEFT: full-page screenshot — fixed to the capture's viewport
		    width so bbox overlays stay aligned with what's drawn. */}
		<Paper variant="outlined" ref={screenshotPaneRef}
			sx={{ width: paneWidth, flex: '0 0 auto', overflow: 'auto', position: 'relative', bgcolor: '#0c1a3e08' }}
		>
			{data.fullpage_screenshot ? (
				<Box sx={{ position: 'relative', display: 'inline-block' }}>
					<img src={data.fullpage_screenshot} alt="Full page"
						// `maxWidth: none` overrides any global `img { max-width: 100% }`
						// rule that would otherwise shrink the screenshot below the
						// declared width on narrow viewports.
						style={{
							display: 'block',
							width: imgWidth, maxWidth: 'none',
							height: 'auto',
						}}
					/>
					{groups.flatMap((g, gIdx) => {
						// Per-item kinds (product-list / legacy brand-logos with logos[])
						// render N rectangles, one per sub. We index the overlay ref
						// by the original sub index AND keep a `__group__` slot
						// (always the first overlay) so a click on the parent card
						// still scrolls to a sensible default.
						const overlays = _overlaysForGroup(g, gIdx)
						return overlays.map((o, oIdx) => (
							<BboxOverlay
								key={`${gIdx}-${oIdx}`}
								bbox={o.bbox}
								label={o.label}
								score={o.score}
								focused={focusedBannerIdx.value === gIdx}
								ref={(el) => {
									const slot = overlayRefs.current[gIdx] || (overlayRefs.current[gIdx] = {})
									if (oIdx === 0) slot.__group__ = el
									if (o.subIdx != null) slot[o.subIdx] = el
								}}
								onClick={() => focusSub(gIdx, o.subIdx, 'screenshot')}
							/>
						))
					})}
				</Box>
			) : (
				<Box sx={{ p: 4, color: 'text.secondary' }}>
					No fullpage screenshot uploaded for this session.
				</Box>
			)}
		</Paper>

		{/* RIGHT: card per group; multi-item groups render parent + indented subs */}
		<Paper variant="outlined" ref={cardsPaneRef}
			sx={{ flex: 1, overflow: 'auto', p: 2 }}
		>
			<QaFilterBar count={needsQaCount} total={allGroups.length} />
			<Stack spacing={2}>
				{groups.map((g, idx) => (
					<GroupCard key={idx} group={g} idx={idx}
						focused={focusedBannerIdx.value === idx}
						onClick={() => focusGroup(idx, 'cards')}
						onSubClick={(subIdx) => focusSub(idx, subIdx, 'cards')}
						subRefSetter={(subIdx, el) => {
							const slot = subRefs.current[idx] || (subRefs.current[idx] = {})
							slot[subIdx] = el
						}}
						ref={el => { groupRefs.current[idx] = el }}
					/>
				))}
				{groups.length === 0 && onlyNeedsQa.value && (
					<Alert severity="success" sx={{ mt: 1 }}>
						Nothing flagged for QA in this session.
					</Alert>
				)}
			</Stack>
		</Paper>
	</Box>
}


function QaFilterBar({ count, total }) {
	const active = onlyNeedsQa.value
	return <Box sx={{
		display: 'flex', alignItems: 'center', gap: 1.5,
		mb: 1.5, px: 1.5, py: 1,
		bgcolor: count > 0 ? '#fff5f5' : '#f8f9fb',
		border: `1px solid ${count > 0 ? '#fecaca' : '#e0e6ee'}`,
		borderRadius: 1,
	}}>
		<Box sx={{
			fontSize: 12, fontWeight: 600,
			color: count > 0 ? '#991b1b' : 'text.secondary',
		}}>
			{count > 0
				? <>⚠ {count} of {total} item-group{total === 1 ? '' : 's'} flagged for QA</>
				: <>All {total} item-group{total === 1 ? '' : 's'} clean — no QA needed</>}
		</Box>
		<Box sx={{ flex: 1 }} />
		<FormControlLabel
			control={
				<Checkbox size="small" checked={active}
					disabled={count === 0}
					onChange={e => { onlyNeedsQa.value = e.target.checked }}
				/>
			}
			label={<Typography variant="caption">Show only needs QA</Typography>}
			sx={{ m: 0 }}
		/>
	</Box>
}


// Hierarchical structure: list of groups. Each group is one banner from the
// session, optionally with sub-items.
function buildGroups(banners) {
	return banners.map((b, bIdx) => {
		const subKey = (b.slides && 'slides')
			|| (b.logos && 'logos')
			|| (b.fragments && 'fragments')
			|| null
		const subs = subKey ? (b[subKey] || []) : []
		// Group bbox: for single-image kinds, the parent's. For multi-item,
		// take the first sub-item's (they typically share the same area).
		const bbox = b.bbox_page || subs[0]?.bbox_page || null
		// Group visibility score: the parent already carries the full
		// container's visibility (and the backend distributes it across
		// sub-items per the slider rules). Fall back to max-of-subs only
		// when the parent has no score (older sessions, edge cases).
		let groupScore = b.visibility_score
		if (groupScore == null) {
			for (const s of subs) {
				if (s.visibility_score != null && (groupScore == null || s.visibility_score > groupScore)) {
					groupScore = s.visibility_score
				}
			}
		}
		// Group viewport_counter: same as the bbox we use.
		const vp = b.viewport_counter != null ? b.viewport_counter : (subs[0]?.viewport_counter ?? null)
		return {
			parent: b,
			subs,
			subKey,
			parentLabel: `${b.kind} ${bIdx + 1}`,
			bbox,
			score: groupScore,
			viewport_counter: vp,
		}
	})
}


// ─── Edit-mode helpers ─────────────────────────────────────────────────
function _setEditing(ids, drafts) {
	editingBannerIds.value = ids
	editDrafts.value = drafts
}

// A session is editable only once its task has gone through AI review.
// The back returns `editable` on the fetch payload (and rejects pre-AI
// saves with 409); this mirrors that so the UI never offers edit
// affordances before the AI baseline exists. Missing field → treat as
// editable (back-compat with older responses).
function isSessionEditable() {
	return sessionData.value?.editable !== false
}

function startEdit(parent) {
	if (!isSessionEditable()) return
	const ids = new Set(editingBannerIds.value)
	const drafts = new Map(editDrafts.value)
	ids.add(parent.id)
	drafts.set(parent.id, JSON.parse(JSON.stringify(parent)))
	_setEditing(ids, drafts)
}

function cancelEdit(id) {
	const ids = new Set(editingBannerIds.value)
	const drafts = new Map(editDrafts.value)
	ids.delete(id)
	drafts.delete(id)
	_setEditing(ids, drafts)
}

function startEditAll() {
	if (!isSessionEditable()) return
	const banners = sessionData.value?.banners || []
	const ids = new Set(banners.map(b => b.id).filter(Boolean))
	const drafts = new Map()
	for (const b of banners) {
		if (b.id) drafts.set(b.id, JSON.parse(JSON.stringify(b)))
	}
	editAllMode.value = true
	_setEditing(ids, drafts)
}

function cancelEditAll() {
	editAllMode.value = false
	_setEditing(new Set(), new Map())
}

async function saveEdit(id, { showToast }) {
	const draft = editDrafts.value.get(id)
	if (!draft) return
	const idSection = sessionData.value?.section?.id_section
	const date = sessionData.value?.date
	const assetPath = sessionData.value?.asset_path || null
	if (!idSection || !date) return
	isSavingEdit.value = true
	try {
		const r = await api.patch(
			`${BACKEND_APIS.VT}/admin/sessions/by-section?` +
			_bySectionQuery(idSection, date, assetPath),
			{ banners: [draft] },
		)
		if (!r.ok) {
			showToast.error('Save failed: ' + r.error)
			return
		}
		sessionData.value = r.data
		showToast.success('Saved')
		cancelEdit(id)
	} finally {
		isSavingEdit.value = false
	}
}

async function saveEditAll({ showToast }) {
	const drafts = Array.from(editDrafts.value.values())
	if (drafts.length === 0) return
	const idSection = sessionData.value?.section?.id_section
	const date = sessionData.value?.date
	const assetPath = sessionData.value?.asset_path || null
	if (!idSection || !date) return
	isSavingEdit.value = true
	try {
		const r = await api.patch(
			`${BACKEND_APIS.VT}/admin/sessions/by-section?` +
			_bySectionQuery(idSection, date, assetPath),
			{ banners: drafts },
		)
		if (!r.ok) {
			showToast.error('Save all failed: ' + r.error)
			return
		}
		sessionData.value = r.data
		showToast.success(`Saved ${drafts.length} banner${drafts.length === 1 ? '' : 's'}`)
		cancelEditAll()
	} finally {
		isSavingEdit.value = false
	}
}

// Append a freshly-created brand to the in-memory catalogue signal so it
// shows up in autocomplete results immediately (no full re-fetch needed).
// Called by BrandCreateDialog on successful create; safe to call repeatedly.
export function _appendBrandToCatalogue(brand) {
	if (!brand?.id_brand || !brand?.name) return
	const list = brandCatalogue.value || []
	if (list.some(b => b.id_brand === brand.id_brand)) return
	brandCatalogue.value = [...list, {
		id_brand: brand.id_brand,
		name: brand.name,
		nameLower: (brand.name || '').toLowerCase(),
	}]
}

export async function ensureBrandCatalogue() {
	if (brandCatalogue.value) return brandCatalogue.value
	try {
		const r = await fetch(BRAND_CATALOGUE_URL, { headers: { Accept: 'application/json' } })
		if (!r.ok) return null
		const data = await r.json()
		brandCatalogue.value = (data.items || []).map(b => ({
			id_brand: b.id_brand,
			name: b.name,
			nameLower: (b.name || '').toLowerCase(),
		}))
		return brandCatalogue.value
	} catch (e) {
		console.warn('Failed to load brand catalogue:', e)
		return null
	}
}


// Editable inline form for a banner (parent + sub-items). Lives inside the
// GroupCard when editingBannerId === parent.id.
function EditingForm({ kind, draft, hasSubs, subKey, group }) {
	const [, force] = useState(0)   // force re-render on mutation
	const refresh = () => force(x => x + 1)

	useEffect(() => { ensureBrandCatalogue().then(refresh) }, [])

	const subtypes = SUBTYPES_BY_KIND[kind] || []
	const showSubtype = kind === 'banner' || kind === 'interstitial' || kind === 'product-slider'
	const showAuto = kind === 'slider' || kind === 'product-slider' || kind === 'brand-logos'
	const showParentBrandsCats = kind === 'banner' || kind === 'interstitial'
	// Single-shot kinds get the QA highlight here; for carousel kinds the
	// flag lives on each sub-item and is handled inside EditingSubForm.
	const parentNeedsQa = !hasSubs && !!draft.ai_review?.needs_qa

	return <Box sx={{
		mt: 2, p: 2,
		bgcolor: parentNeedsQa ? '#fff5f5' : '#fafbfc',
		border: parentNeedsQa
			? `2px solid ${NEEDS_QA_COLOR}`
			: '1px dashed #c7d2e0',
		borderRadius: 2,
	}}>
		<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
			<SectionLabel>Editing</SectionLabel>
			<NeedsQABadge aiReview={draft.ai_review} />
		</Stack>
		<Stack spacing={2}>
			{showSubtype && (
				<TextField select size="small" label="Subtype" sx={{ minWidth: 200 }}
					value={draft.type || ''}
					onChange={e => { draft.type = e.target.value; refresh() }}
				>
					{subtypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
				</TextField>
			)}
			{showAuto && (
				<FormControlLabel
					control={<Checkbox checked={!!draft.auto_switch}
						onChange={e => { draft.auto_switch = e.target.checked; refresh() }} />}
					label="Auto-switch (rotates on its own)"
				/>
			)}
			{showParentBrandsCats && (
				<>
					<EditCategories value={draft.categories || []} onChange={v => { draft.categories = v; refresh() }} />
					<EditBrands value={draft.brands || []} onChange={v => { draft.brands = v; refresh() }} />
					<UnmatchedBrandChips
						aiReview={draft.ai_review}
						onResolve={(raw, canonical) => _resolveUnmatched(draft, raw, canonical, refresh)}
					/>
				</>
			)}
			{/* A single-shot banner IS its own leaf — it has no sub-items, so
			    without this there is nowhere to say that THIS banner ran as a
			    paid placement. That was the gap: the fields existed only on
			    product-list fragments (869ehankw, step B.8). Rendered for
			    every kind; on a carousel the parent is a container and the
			    real answer belongs to each slide, which has its own pair. */}
			{!hasSubs && (
				<EyecatchEditor value={asStringList(draft.eyecatch)}
					onChange={v => { draft.eyecatch = v; refresh() }} />
			)}
			{/* Sponsored is NOT gated to `!hasSubs` any more (869ehaxqf). The
			    comment above was right that a slide's own badge belongs to
			    that slide, and it still does — the per-sub pair below is
			    untouched. What it missed is the case where the PLACEMENT is
			    the whole strip: the retailer paints one "Publicidad" above
			    the carousel, outside every slide's bbox, so no slide can
			    carry it and there was nowhere to record it. The export
			    inherits this down to every slide and nested product.
			    `eyecatch` stays gated: it is descriptive, container-level
			    labels were never asked for, and inheriting them would move
			    historical numbers. */}
			<FormControlLabel
				control={<Checkbox size="small" checked={!!draft.sponsored}
					onChange={e => { draft.sponsored = e.target.checked; refresh() }} />}
				label={hasSubs ? 'Sponsored (todo el carrusel)' : 'Sponsored'}
			/>
			<TextField size="small" label="Destination URL"
				value={draft.destination_url || ''}
				onChange={e => { draft.destination_url = e.target.value || null; refresh() }}
			/>
			<TextField size="small" label="Notes" multiline rows={2}
				value={draft.notes || ''}
				onChange={e => { draft.notes = e.target.value; refresh() }}
			/>
		</Stack>

		{hasSubs && (() => {
			const allSubs = draft[subKey] || []
			// Same QA filter as the read-only view: when the toggle is on,
			// only show sub-items that still need attention. Editing the
			// validated ones can still be reached by turning the toggle off.
			const visible = onlyNeedsQa.value
				? allSubs
					.map((s, i) => ({ s, i }))
					.filter(({ s }) => s?.ai_review?.needs_qa)
				: allSubs.map((s, i) => ({ s, i }))
			if (visible.length === 0) return null
			return <Box sx={{ mt: 2 }}>
				<SectionLabel>{subKey}</SectionLabel>
				<Stack spacing={1.5} sx={{ mt: 1 }}>
					{visible.map(({ s, i }) => (
						<EditingSubForm key={s.id || i} kind={kind} subKey={subKey}
							sub={s} index={i + 1} onChange={refresh}
							group={group} subIdx={i} />
					))}
				</Stack>
			</Box>
		})()}
	</Box>
}


function EditingSubForm({ kind, subKey, sub, index, onChange, group, subIdx }) {
	const showType = subKey === 'slides' && kind === 'slider'
	// Categories + brands editable for slider/product-slider slides AND
	// product-list fragments (each fragment is one product, brand applies
	// at item level same as for slider slides).
	const showCatsBrands =
		(subKey === 'slides' && (kind === 'slider' || kind === 'product-slider')) ||
		(subKey === 'fragments' && kind === 'product-list')
	// brand-logos slides → brands only, no categories.
	const showBrandsOnly = (subKey === 'slides' && kind === 'brand-logos')
	// Legacy: pre-refactor brand-logos used `logos` with a single brand.
	const showSingleBrand = subKey === 'logos'
	// 869ehb2zd — which shape a leaf has is decided by the LEAF, not by its
	// container. A product-slider is captured per product post-cutover, so
	// one of its slides can be a single product while a historical pair crop
	// sits right next to it in the same array. `sub.leaf_kind` is what the
	// extension stamps; absent means pre-cutover.
	const leafKind = sub.leaf_kind
		|| (kind === 'product-slider' && subKey === 'slides' ? 'product-slide' : null)
		|| (kind === 'product-list' && subKey === 'fragments' ? 'product' : null)
	// Single-product per sub-item: a product-list fragment, and now also a
	// product cut out of a slider slide. The flat product/eyecatch/sponsored
	// fields live at sub-item level.
	const showSingleProductFields = leafKind === 'product'
	// Multi-product per sub-item: a HISTORICAL product-slider slide showing
	// several product cards in one image. The data lives in a `products[]`
	// array with one entry per visible product, edited via ProductsEditor
	// below. New captures never produce this.
	const showProductsArray = leafKind === 'product-slide'
	const childLabel = subKey.replace(/s$/, '')
	const score = sub.visibility_score
	const color = visibilityColor(score || 0)
	const subNeedsQa = !!sub.ai_review?.needs_qa
	return <Box sx={{
		p: 1.5,
		bgcolor: subNeedsQa ? '#fff5f5' : '#fff',
		border: subNeedsQa ? `2px solid ${NEEDS_QA_COLOR}` : '1px solid #d0d7de',
		boxShadow: subNeedsQa ? `0 0 0 3px ${NEEDS_QA_COLOR}22` : 'none',
		borderRadius: 1, display: 'flex', gap: 2,
	}}>
		{/* Thumbnail kept visible while editing — keeps the QA grounded
		    in which slide they're currently changing. */}
		{sub.crop ? (
			<img src={sub.crop} alt=""
				onClick={() => {
					if (group) openGroupLightbox(group, subIdx ?? 0)
					else lightboxSrc.value = sub.crop
				}}
				style={{
					width: 160, height: 160, objectFit: 'contain',
					borderRadius: 4, border: `2px solid ${color}`,
					background: '#f8f9fb',
					flex: '0 0 auto',
					cursor: 'zoom-in',
				}}
				onError={(e) => { console.warn("[VT review] crop failed to load for", sub.id || index, "crop type:", typeof sub.crop, "starts:", String(sub.crop).slice(0, 30)) }}
			/>
		) : (
			<Box sx={{
				width: 160, height: 160, flex: '0 0 auto',
				bgcolor: '#fff7e6', border: '2px dashed #ffd591', borderRadius: 1,
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				color: '#874d00', fontSize: 11, textAlign: 'center', p: 1,
			}}>
				No crop available<br/>(sub.crop is empty)
			</Box>
		)}
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Box sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
				<span>{childLabel} {index}</span>
				<Stack direction="row" spacing={0.75} alignItems="center">
					<NeedsQABadge aiReview={sub.ai_review} />
					{score != null && (
						<span style={{
							background: color, color: '#fff',
							fontSize: 11, fontWeight: 800,
							padding: '2px 8px', borderRadius: 4,
						}}>{score.toFixed(1)}%</span>
					)}
				</Stack>
			</Box>
			<Stack spacing={1.5}>
				{showType && (
					<TextField select size="small" label="Subtype" sx={{ minWidth: 180 }}
						value={sub.type || ''}
						onChange={e => { sub.type = e.target.value; onChange() }}
					>
						{SUBTYPES_BY_KIND.slider.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
					</TextField>
				)}
				{showCatsBrands && (
					<>
						<EditCategories value={sub.categories || []} onChange={v => { sub.categories = v; onChange() }} />
						<EditBrands value={sub.brands || []} onChange={v => { sub.brands = v; onChange() }} />
					</>
				)}
				{showBrandsOnly && (
					<EditBrands value={sub.brands || []} onChange={v => { sub.brands = v; onChange() }} />
				)}
				{showSingleBrand && (
					<EditSingleBrand value={sub.brand || null} onChange={v => { sub.brand = v; onChange() }} />
				)}
				{(showCatsBrands || showBrandsOnly || showSingleBrand) && (
					<UnmatchedBrandChips
						aiReview={sub.ai_review}
						onResolve={(raw, canonical) => _resolveUnmatched(sub, raw, canonical, onChange)}
					/>
				)}
				{showSingleProductFields && (
					<TextField size="small" label="Product"
						placeholder="Sauvage Eau de Parfum 100ml"
						value={sub.product || ''}
						onChange={e => { sub.product = e.target.value; onChange() }}
					/>
				)}
				{/* Eye-catch and Sponsored are NOT gated to product-list any
				    more (task 869ehankw, step B.8). They were, and that is why
				    there was no way to mark a BANNER as sponsored — the one
				    element type where a sponsored placement matters most.
				    They describe the sighting in front of you, whatever its
				    kind, and this is where an operator fixes what the DOM
				    extraction missed or got wrong. */}
				<EyecatchEditor value={asStringList(sub.eyecatch)}
					onChange={v => { sub.eyecatch = v; onChange() }} />
				<FormControlLabel
					control={
						<Checkbox size="small" checked={!!sub.sponsored}
							onChange={e => { sub.sponsored = e.target.checked; onChange() }}
						/>
					}
					label="Sponsored"
				/>
				{showProductsArray && (
					<ProductsEditor
						products={sub.products || []}
						onChange={(next) => { sub.products = next; onChange() }}
					/>
				)}
				<TextField size="small" label="Destination URL"
					value={sub.destination_url || ''}
					onChange={e => { sub.destination_url = e.target.value || null; onChange() }}
				/>
				<TextField size="small" label="Notes" multiline rows={1}
					value={sub.notes || ''}
					onChange={e => { sub.notes = e.target.value; onChange() }}
				/>
			</Stack>
		</Box>
	</Box>
}


// Editable list of products inside a product-slider slide. Each slide
// can show 1-3 product cards, so the data model carries a `products[]`
// array; this widget lets the QA add / remove / edit each card with
// brand picker + free-form name + eye-catch tags + sponsored flag.
export function ProductsEditor({ products, onChange }) {
	const list = Array.isArray(products) ? products : []
	const update = (i, patch) => {
		const next = list.map((p, j) => j === i ? { ...p, ...patch } : p)
		onChange(next)
	}
	const remove = (i) => onChange(list.filter((_, j) => j !== i))
	const add = () => onChange([
		...list,
		{ id: 'p_' + Math.random().toString(36).slice(2, 9), name: '', brands: [], categories: [], eyecatch: [], sponsored: false },
	])

	return <Box sx={{ p: 1.5, bgcolor: '#f8f9fb', border: '1px solid #d0d7de', borderRadius: 1 }}>
		<Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
			<SectionLabel>Products in this slide ({list.length})</SectionLabel>
			<Box sx={{ flex: 1 }} />
			<Button size="small" variant="outlined" onClick={add}>+ Add product</Button>
		</Stack>
		{list.length === 0 ? (
			<Typography variant="caption" sx={{ color: 'text.secondary' }}>
				No products yet. Click "+ Add product" or run AI review to populate.
			</Typography>
		) : (
			<Stack spacing={1.25}>
				{list.map((p, i) => (
					<Box key={p.id || i} sx={{ p: 1.25, bgcolor: '#fff', border: '1px solid #e0e6ee', borderRadius: 1 }}>
						<Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
							<Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
								Product {i + 1}
							</Typography>
							<Box sx={{ flex: 1 }} />
							<IconButton size="small" onClick={() => remove(i)}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Stack>
						<Stack spacing={1}>
							<TextField size="small" label="Name"
								placeholder="Sauvage Eau de Parfum 100ml"
								value={p.name || ''}
								onChange={e => update(i, { name: e.target.value })}
							/>
							<EditBrands value={p.brands || []}
								onChange={v => update(i, { brands: v })} />
							<EditCategories value={p.categories || []}
								onChange={v => update(i, { categories: v })} />
							<EyecatchEditor value={asStringList(p.eyecatch)}
								onChange={v => update(i, { eyecatch: v })} />
							<FormControlLabel
								control={
									<Checkbox size="small" checked={!!p.sponsored}
										onChange={e => update(i, { sponsored: e.target.checked })}
									/>
								}
								label="Sponsored"
							/>
						</Stack>
					</Box>
				))}
			</Stack>
		)}
	</Box>
}


// Eye-catch labels are free-form (no canonical catalogue): the AI returns
// whatever it sees on the card ("oferta", "novedad", "-20%", "exclusivo")
// and the human can add/remove. We use Autocomplete in `freeSolo` mode
// so the QA can type and add new entries with Enter.
export function EyecatchEditor({ value, onChange }) {
	return <Autocomplete
		multiple
		freeSolo
		size="small"
		options={[]}
		value={value || []}
		onChange={(_, v) => onChange(
			(v || []).map(s => (typeof s === 'string' ? s : '').trim().toLowerCase()).filter(Boolean)
		)}
		renderTags={(tagValue, getTagProps) =>
			tagValue.map((option, i) => (
				<Chip key={i} label={option} size="small"
					{...getTagProps({ index: i })}
					sx={{ bgcolor: '#fef3c7', color: '#854d0e', fontWeight: 600 }}
				/>
			))
		}
		renderInput={(params) => (
			<TextField {...params} label="Eye-catch" size="small"
				placeholder="oferta, novedad, mejor precio…"
				helperText="Press Enter to add. Free-form labels printed on the product card."
			/>
		)}
	/>
}


export function EditCategories({ value, onChange }) {
	const set = new Set(value)
	return <Box>
		<SectionLabel>Categories</SectionLabel>
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
			{CATEGORIES.map(c => {
				const on = set.has(c)
				const palette = CATEGORY_PALETTE[c] || {}
				return <Chip key={c} label={c}
					onClick={() => {
						const next = new Set(set)
						on ? next.delete(c) : next.add(c)
						onChange(Array.from(next))
					}}
					sx={{
						cursor: 'pointer', textTransform: 'uppercase',
						letterSpacing: '0.04em',
						fontWeight: 700,
						height: 30,
						transition: 'all 0.12s',
						// Selected: full saturated palette colour with white text +
						// a glow ring so it pops against the unselected siblings.
						// Unselected: greyed out with dashed border and faded
						// label, so the contrast is unambiguous.
						...(on ? {
							bgcolor: palette.fg,
							color: '#fff',
							border: `2px solid ${palette.fg}`,
							boxShadow: `0 0 0 3px ${palette.bg}`,
						} : {
							bgcolor: 'transparent',
							color: '#9aa1ad',
							border: '2px dashed #c7d0db',
							opacity: 0.7,
						}),
						'&:hover': on ? {
							filter: 'brightness(0.92)',
						} : {
							borderColor: palette.border,
							color: palette.fg,
							opacity: 1,
						},
					}}
				/>
			})}
		</Box>
	</Box>
}


export function EditBrands({ value, onChange }) {
	const cat = brandCatalogue.value || []
	// Quick-add the canonical "Unknown Brand" — resolved from the
	// catalogue by name so we never hard-code an id. Frequently needed
	// when the creative shows no legible brand.
	const unknown = cat.find(o => o.nameLower === 'unknown brand')
	const hasUnknown = !!unknown && (value || []).some(b => b.id_brand === unknown.id_brand)
	const addUnknown = () => {
		if (!unknown || hasUnknown) return
		onChange([...(value || []), { id_brand: unknown.id_brand, name: unknown.name }])
	}
	return <Box>
		<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
			<Box sx={{ mb: 0 }}><SectionLabel>Brands</SectionLabel></Box>
			{unknown && (
				<Chip
					size="small"
					variant="filled"
					label="+ Unknown Brand"
					onClick={addUnknown}
					disabled={hasUnknown}
					sx={{
						height: 20,
						fontSize: 10,
						fontWeight: 700,
						cursor: hasUnknown ? 'default' : 'pointer',
						bgcolor: '#f59e0b',
						color: '#1f2937',
						'&:hover': { bgcolor: '#d97706' },
					}}
				/>
			)}
		</Stack>
		<Autocomplete
			multiple size="small"
			options={cat}
			value={value}
			isOptionEqualToValue={(a, b) => a.id_brand === b.id_brand}
			getOptionLabel={(o) => o.name || ''}
			filterOptions={(opts, state) => {
				const q = (state.inputValue || '').toLowerCase().trim()
				if (!q) return opts.slice(0, 50)
				const out = []
				for (const o of opts) {
					if (o.nameLower?.startsWith(q)) out.push(o)
					if (out.length >= 50) break
				}
				if (out.length === 0) {
					for (const o of opts) {
						if (o.nameLower?.includes(q)) out.push(o)
						if (out.length >= 50) break
					}
				}
				return out
			}}
			onChange={(_, v) => onChange(v.map(b => ({ id_brand: b.id_brand, name: b.name })))}
			renderInput={(params) => <TextField {...params}
				placeholder="Type to search…"
				helperText={cat.length === 0 ? "Loading catalogue…" : null}
			/>}
		/>
	</Box>
}


function EditSingleBrand({ value, onChange }) {
	const cat = brandCatalogue.value || []
	return <Box>
		<SectionLabel>Brand</SectionLabel>
		<Autocomplete
			size="small"
			options={cat}
			value={value}
			isOptionEqualToValue={(a, b) => a.id_brand === b.id_brand}
			getOptionLabel={(o) => o?.name || ''}
			onChange={(_, v) => onChange(v ? { id_brand: v.id_brand, name: v.name } : null)}
			renderInput={(params) => <TextField {...params}
				placeholder="Type to search…"
				helperText={cat.length === 0 ? "Loading catalogue…" : null}
			/>}
		/>
	</Box>
}


// One overlay per group. For multi-item groups (sliders) we don't paint N
// stacked rectangles — they're all in the same place. Two badges:
//  - top-left: counter (and N items, if multi)
//  - top-right: visibility percentage
const BboxOverlay = React.forwardRef(function BboxOverlay({
	bbox, label, score, focused, onClick,
}, ref) {
	if (!bbox) return null
	const color = visibilityColor(score || 0)
	const stroke = focused ? 6 : 4
	return <Box ref={ref}
		onClick={onClick}
		sx={{
			position: 'absolute',
			left: bbox.x, top: bbox.y, width: bbox.width, height: bbox.height,
			border: `${stroke}px solid ${color}`,
			boxShadow: focused ? `0 0 0 2px #fff inset, 0 0 14px ${color}cc` : `0 0 0 1px #fff66 inset`,
			cursor: 'pointer',
			zIndex: focused ? 5 : 1,
			transition: 'box-shadow 0.15s, border-width 0.15s',
			boxSizing: 'border-box',
		}}
	>
		{/* Top-left: label */}
		<Box sx={{
			position: 'absolute', top: -stroke, left: -stroke,
			bgcolor: color, color: '#fff',
			fontSize: 11, fontWeight: 700,
			padding: '2px 7px', borderRadius: '0 0 4px 0',
			fontFamily: 'monospace',
			minWidth: 22, textAlign: 'center',
		}}>
			{label}
		</Box>
		{/* Top-right: visibility percentage — big, primary metric */}
		<Box sx={{
			position: 'absolute', top: -stroke, right: -stroke,
			bgcolor: color, color: '#fff',
			fontSize: 18, fontWeight: 800,
			padding: '4px 12px', borderRadius: '0 0 0 6px',
			fontFamily: 'monospace',
			letterSpacing: '-0.02em',
			boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
			lineHeight: 1.1,
		}}>
			{score != null ? `${score.toFixed(1)}%` : '?'}
		</Box>
	</Box>
})


// Decide how many rectangles a group renders on the screenshot pane.
// Most kinds collapse to ONE rect at the parent's bbox (sliders / brand-
// logos with slides[] all share the same container, drawing each sub
// would just stack identical rectangles). Per-item kinds — product-list
// fragments, legacy brand-logos logos — live at distinct page positions
// and only render correctly if we draw each one separately.
function _overlaysForGroup(g, gIdx) {
	const kind = g.parent?.kind
	const subs = g.subs || []
	// Interstitials don't get an overlay: they cover the whole screen, the
	// bbox we stored is just the dialog crop, and the screenshot doesn't
	// include the popup anyway. Visibility is always 100% by definition.
	if (kind === 'interstitial') return []
	const isPerItem =
		(kind === 'product-list' && g.subKey === 'fragments') ||
		(kind === 'brand-logos' && g.subKey === 'logos')
	if (isPerItem) {
		// Keep the ORIGINAL sub index so SubItemRow clicks (which know
		// their position in the parent's subs[]) can map back to the
		// right rectangle, even when subs without a bbox_page get filtered
		// out and the overlay array becomes sparse.
		return subs
			.map((s, i) => ({ s, i }))
			.filter(({ s }) => s.bbox_page)
			.map(({ s, i }) => ({
				bbox:  s.bbox_page,
				label: `${gIdx + 1}.${i + 1}`,
				score: s.visibility_score,
				subIdx: i,
			}))
	}
	if (!g.bbox) return []
	const subCount = subs.length
	return [{
		bbox:  g.bbox,
		label: `#${gIdx + 1}${subCount > 0 ? ` · ${subCount}` : ''}`,
		score: g.score,
		subIdx: null,   // group-level overlay, not tied to a specific sub
	}]
}


// Mini-collage shown in the GroupCard header for carousel kinds whose
// parent has no crop of its own (sliders, product-sliders, brand-logos
// with slides[]). Renders a 2×2 grid of the first 4 sub-item crops; if
// there are more, the last cell shows a "+N" overlay so the QA still
// knows the slider has more slides than fit. Click anywhere → opens the
// lightbox starting at slide 1.
function SubsCollage({ subs, color, kind, childLabel, group }) {
	const withCrops = (subs || []).filter(s => s?.crop)
	const cells = withCrops.slice(0, 4)
	const overflow = Math.max(0, withCrops.length - 4)
	// 1 sub → fill the box; 2 → side-by-side; 3-4 → 2×2.
	const cols = cells.length === 1 ? 1 : 2
	const rows = cells.length <= 2 ? 1 : 2
	if (cells.length === 0) {
		// Defensive fallback: no crops at all on the subs (shouldn't happen
		// but better than a blank box). Keeps the original "kind · N items"
		// label so the QA at least sees what's going on.
		return <Box
			sx={{
				width: 260, height: 260, borderRadius: 1, flex: '0 0 auto',
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				bgcolor: '#f8f9fb', border: `2px dashed ${color}`,
				color, fontWeight: 700, fontSize: 18, textAlign: 'center',
			}}
		>
			{kind}<br />{subs.length} {childLabel}{subs.length === 1 ? '' : 's'}
		</Box>
	}
	return <Box
		onClick={(e) => { e.stopPropagation(); openGroupLightbox(group, 0) }}
		sx={{
			width: 260, height: 260, flex: '0 0 auto',
			borderRadius: 1.5, overflow: 'hidden',
			border: `2px solid ${color}`,
			bgcolor: '#f8f9fb',
			cursor: 'zoom-in',
			position: 'relative',
			display: 'grid',
			gridTemplateColumns: `repeat(${cols}, 1fr)`,
			gridTemplateRows:    `repeat(${rows}, 1fr)`,
			gap: '2px',
		}}
	>
		{cells.map((s, i) => {
			const isLastWithOverflow = (i === cells.length - 1) && overflow > 0
			return <Box key={i} sx={{
				position: 'relative',
				bgcolor: '#fff',
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				overflow: 'hidden',
			}}>
				<img src={s.crop} alt=""
					style={{
						maxWidth: '100%', maxHeight: '100%',
						objectFit: 'contain',
						display: 'block',
					}}
				/>
				{isLastWithOverflow && (
					<Box sx={{
						position: 'absolute', inset: 0,
						bgcolor: 'rgba(12, 26, 62, 0.62)',
						color: '#fff',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em',
					}}>
						+{overflow}
					</Box>
				)}
			</Box>
		})}
	</Box>
}


// Group card: parent's metadata on top, sub-items indented underneath. The
// hierarchy still shows that a slider contains N slides, but the layout
// gives clear visual primacy to the three things the QA cares about most:
// 1) what kind of element it is, 2) brands present, 3) the visibility %.
const GroupCard = React.forwardRef(function GroupCard({ group, idx, focused, onClick, onSubClick, subRefSetter }, ref) {
	const { showToast } = useAppToast()
	const { parent, subs, subKey, parentLabel } = group
	const score = group.score
	const color = visibilityColor(score || 0)
	const subtype = parent.type
	// Banner / interstitial / brand-logos parent keeps its own categories +
	// brands; sliders push those to per-slide.
	const parentCats = parent.categories || []
	const parentBrands = parent.brands || []
	const hasSubs = subs && subs.length > 0
	const childLabel = subKey ? subKey.replace(/s$/, '') : 'item'

	// For multi-item kinds, aggregate brands across all sub-items so the
	// header gives a quick view of every brand in the slider as a whole.
	const aggregatedBrands = hasSubs ? aggregateBrands(subs) : parentBrands

	const isEditingThis = editingBannerIds.value.has(parent.id)
	const draft = isEditingThis ? editDrafts.value.get(parent.id) : null
	// Per-card buttons hide while edit-all is on; the only controls then
	// are the global Save all / Cancel all in the header.
	const showPerCardButtons = !editAllMode.value && isSessionEditable()
	// Highlight the whole card only for single-shot kinds (banner /
	// interstitial) flagged as needs_qa. For carousel kinds we highlight
	// per sub-item inside SubItemRow — the user explicitly asked that
	// "if a slide needs QA, only that slide is marked, not the whole
	// slider".
	const parentNeedsQa = !hasSubs && !!parent.ai_review?.needs_qa

	return <Paper ref={ref}
		variant="outlined"
		onClick={isEditingThis ? undefined : onClick}
		sx={{
			p: 0, cursor: isEditingThis ? 'default' : 'pointer',
			// Stronger default outline so each parent+children block reads
			// as ONE unit and the boundary between groups is obvious. Focus
			// / edit / needs_qa override with their own colour as before.
			borderColor: isEditingThis
				? '#2563eb'
				: (parentNeedsQa ? NEEDS_QA_COLOR : (focused ? color : '#0c1a3e')),
			borderWidth: 2,
			borderRadius: 2,
			boxShadow: isEditingThis
				? `0 0 0 3px #2563eb33`
				: (parentNeedsQa
					? `0 0 0 3px ${NEEDS_QA_COLOR}33`
					: (focused
						? `0 0 0 3px ${color}33`
						: '0 2px 8px rgba(12, 26, 62, 0.10)')),
			overflow: 'hidden',
		}}
	>
		{/* Parent header */}
		<Box sx={{
			display: 'flex', gap: 2.5, p: 2.5,
			// Tinted background so the parent reads visually as the "header"
			// of the group; children stay on plain #fafbfc and the contrast
			// makes the hierarchy obvious without extra connectors / guides.
			bgcolor: '#e2e8f0',
		}}>
			{parent.crop ? (
				<img src={parent.crop} alt=""
					onClick={(e) => { e.stopPropagation(); lightboxSrc.value = parent.crop }}
					style={{
						width: 260, height: 260, objectFit: 'contain',
						borderRadius: 6, border: `2px solid ${color}`,
						background: '#f8f9fb',
						flex: '0 0 auto',
						cursor: 'zoom-in',
					}} />
			) : hasSubs ? (
				<SubsCollage subs={subs} color={color} kind={parent.kind}
					childLabel={childLabel} group={group} />
			) : (
				<Box sx={{ width: 260, height: 260, bgcolor: 'grey.200', borderRadius: 1, flex: '0 0 auto' }} />
			)}
			<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
				{/* TOP ROW: kind (big) + score (huge) + edit controls ──────────── */}
				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.25, flexWrap: 'wrap' }}>
							<Box sx={{
								fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em',
								color: '#0c1a3e', textTransform: 'uppercase',
								lineHeight: 1.1, fontFamily: 'var(--font-heading), inherit',
							}}>
								{parent.kind}
							</Box>
							{(parent.kind === 'slider' || parent.kind === 'product-slider' || parent.kind === 'brand-logos') && (
								<AutoModeBadge auto={!!parent.auto_switch} />
							)}
						</Stack>
						<Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
							{parentLabel}
							{subtype && <> · subtype <strong style={{ color: '#0c1a3e' }}>{subtype}</strong></>}
							{hasSubs && <> · {subs.length} {childLabel}{subs.length === 1 ? '' : 's'}</>}
						</Box>
					</Box>
					<VisibilityBadge score={score} viewport={group.viewport_counter}
						isContainer={hasSubs && (parent.kind === 'slider' || parent.kind === 'product-slider' || parent.kind === 'brand-logos')}
						isAuto={!!parent.auto_switch}
					/>
					{/* Single-shot kinds (banner, interstitial) tag at the parent
					    level. Carousel parents stay untagged — their children
					    show the badge inside SubItemRow. */}
					<NeedsQABadge aiReview={parent.ai_review} />
					{showPerCardButtons && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
							onClick={e => e.stopPropagation()}
						>
							{!isEditingThis && (
								<Button size="small" variant="outlined"
									startIcon={<EditIcon fontSize="small" />}
									onClick={() => startEdit(parent)}
								>Edit</Button>
							)}
							{isEditingThis && (
								<>
									<Button size="small" variant="contained" color="primary"
										startIcon={isSavingEdit.value
											? <CircularProgress size={14} color="inherit" />
											: <SaveIcon fontSize="small" />}
										disabled={isSavingEdit.value}
										onClick={() => saveEdit(parent.id, { showToast })}
									>{isSavingEdit.value ? 'Saving…' : 'Save'}</Button>
									<Button size="small" variant="outlined" color="inherit"
										startIcon={<CloseIcon fontSize="small" />}
										disabled={isSavingEdit.value}
										onClick={() => cancelEdit(parent.id)}
									>Cancel</Button>
								</>
							)}
						</Box>
					)}
				</Box>

				{/* BRANDS — most prominent metadata after score ─────────────── */}
				{aggregatedBrands.length > 0 && (
					<Box sx={{ mb: 1.5 }}>
						<SectionLabel>Brands</SectionLabel>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
							{aggregatedBrands.map((b, i) => (
								<BrandChip key={i} brand={b} />
							))}
						</Box>
					</Box>
				)}

				{/* AI suggestions that didn't match any canonical brand. Shown
				    so the admin sees them and can decide whether to add them
				    to the brand catalogue. */}
				<UnmatchedBrandChips aiReview={parent.ai_review} />

				{/* CATEGORIES ────────────────────────────────────────────────── */}
				{(hasSubs ? aggregateCategories(subs) : parentCats).length > 0 && (
					<Box sx={{ mb: 1.5 }}>
						<SectionLabel>Categories</SectionLabel>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
							{(hasSubs ? aggregateCategories(subs) : parentCats).map(c => (
								<CategoryChip key={c} category={c} />
							))}
						</Box>
					</Box>
				)}

				{/* TECHNICAL DETAILS ─────────────────────────────────────────── */}
				<Box sx={{ mt: 'auto', pt: 1, borderTop: '1px dashed #e0e6ee' }}>
					{group.bbox && (
						<Box sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>
							bbox: x={Math.round(group.bbox.x)} y={Math.round(group.bbox.y)}
							{' · '}{Math.round(group.bbox.width)}×{Math.round(group.bbox.height)}
						</Box>
					)}
					{parent.destination_url && (
						<Box sx={{ fontSize: 11, color: 'text.disabled', wordBreak: 'break-all', mt: 0.25 }}>
							→ {parent.destination_url}
						</Box>
					)}
					{parent.notes && (
						<Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5, fontStyle: 'italic' }}>
							“{parent.notes}”
						</Box>
					)}
				</Box>
			</Box>
		</Box>

		{/* Editing form, when this banner is the one being edited. Replaces
		    the read-only sub-items below by editable controls. */}
		{isEditingThis && draft && (
			<Box sx={{ px: 2.5, pb: 2.5 }} onClick={e => e.stopPropagation()}>
				<EditingForm
					kind={parent.kind}
					draft={draft}
					hasSubs={hasSubs}
					subKey={subKey}
					group={group}
				/>
			</Box>
		)}

		{/* Sub-items, indented under the parent. Shown only when NOT editing
		    (during edit, the form covers them). The visual hierarchy is
		    reinforced by:
		      - a "transition strip" labelled "↳ N slides" in the parent's
		        colour, sitting between header and children,
		      - a thick coloured left guide bar that runs the full height
		        of the children block,
		      - a small horizontal connector ("──") on each child that
		        attaches it to the guide. */}
		{hasSubs && !isEditingThis && (() => {
			// Honour the "Only needs QA" filter at the sub-item level too:
			// inside a visible group, hide the children that are already
			// validated. Original positions are preserved (slide #2 stays
			// labelled "slide 2" even when slide 1 is hidden).
			const visible = onlyNeedsQa.value
				? subs
					.map((s, i) => ({ s, i }))
					.filter(({ s }) => s?.ai_review?.needs_qa)
				: subs.map((s, i) => ({ s, i }))
			if (visible.length === 0) return null
			const filtered = visible.length !== subs.length
			return <Box sx={{
				bgcolor: '#fafbfc',
				borderTop: `1px solid ${color}33`,
			}}>
				{/* Transition strip — explicit "these are the children" cue */}
				<Box sx={{
					display: 'flex', alignItems: 'center', gap: 1,
					px: 2, py: 0.75,
					fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
					textTransform: 'uppercase',
					color, bgcolor: `${color}11`,
					borderBottom: `1px dashed ${color}66`,
				}}>
					<Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>↳</Box>
					{visible.length} {childLabel}{visible.length === 1 ? '' : 's'}
					{filtered && (
						<Box component="span" sx={{
							fontSize: 9, fontWeight: 600, letterSpacing: '0.04em',
							color: 'text.secondary', textTransform: 'none',
						}}>
							(of {subs.length}, filtered)
						</Box>
					)}
				</Box>
				{/* Children list */}
				<Stack spacing={1.25} sx={{ pl: 2, pr: 2, py: 1.5 }}>
					{visible.map(({ s, i }) => (
						<SubItemRow key={i} sub={s} index={i + 1} subKey={subKey} childLabel={childLabel}
							parentKind={parent.kind}
							group={group} subIdx={i}
							onClick={onSubClick ? () => onSubClick(i) : undefined}
							rowRef={subRefSetter ? (el => subRefSetter(i, el)) : undefined} />
					))}
				</Stack>
			</Box>
		})()}
	</Paper>
})


// Brand suggestions the AI proposed but couldn't be matched to a canonical
// brand record. Shown so the admin can either add them to the catalogue
// (if legit) or treat them as typos / hallucinations.
export function UnmatchedBrandChips({ aiReview, onResolve }) {
	// `onResolve(rawString, canonical)` is provided by edit-mode parents
	// (EditingForm / EditingSubForm). Read-only call sites (GroupCard /
	// SubItemRow) omit it and the chips stay non-interactive.
	const [anchorEl, setAnchorEl] = useState(null)
	const [activeRaw, setActiveRaw] = useState(null)
	const [pickedBrand, setPickedBrand] = useState(null)
	const [busy, setBusy] = useState(false)
	const [createOpen, setCreateOpen] = useState(false)
	const list = aiReview?.unmatched_brands || []
	if (list.length === 0) return null

	const openPopover = (e, raw) => {
		setAnchorEl(e.currentTarget)
		setActiveRaw(raw)
		setPickedBrand(null)
	}
	const closePopover = () => {
		setAnchorEl(null)
		setActiveRaw(null)
		setPickedBrand(null)
	}
	const confirmMapping = async () => {
		if (!activeRaw || !pickedBrand?.id_brand) return
		setBusy(true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/brand-mapping`, {
			raw: activeRaw,
			id_brand: pickedBrand.id_brand,
		})
		setBusy(false)
		if (!r.ok) {
			alert(`Failed to record mapping: ${r.error}`)
			return
		}
		onResolve(activeRaw, { id_brand: pickedBrand.id_brand, name: pickedBrand.name })
		closePopover()
	}
	const handleCreated = async (newBrand) => {
		// Persist the raw→canonical mapping so the SAME raw string never
		// resurfaces as "unmatched" in future AI runs — same contract as
		// the map-to-existing path above.
		const m = await api.post(`${BACKEND_APIS.VT}/admin/brand-mapping`, {
			raw: activeRaw,
			id_brand: newBrand.id_brand,
		})
		if (!m.ok) {
			alert(`Brand created (id ${newBrand.id_brand}) but failed to record the auto-mapping: ${m.error}. The brand exists; future AI runs may still flag this raw string until you map it manually.`)
		}
		onResolve(activeRaw, { id_brand: newBrand.id_brand, name: newBrand.name })
		setCreateOpen(false)
		closePopover()
	}

	const interactive = typeof onResolve === 'function'
	const cat = brandCatalogue.value || []
	return <>
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
			<SectionLabel>AI unmatched</SectionLabel>
			{list.map((s, i) => (
				<Tooltip key={i} title={interactive
					? "Click to map this raw string to a canonical brand. The mapping is recorded so future AI reviews resolve it automatically."
					: "AI suggested this brand but it isn't in the catalog — typo or missing entry?"}
				>
					<Chip label={s} size="small"
						onClick={interactive ? (e) => openPopover(e, s) : undefined}
						clickable={interactive}
						sx={{
							fontSize: 11, fontStyle: 'italic',
							bgcolor: '#fff7ed', color: '#9a3412',
							border: '1.5px dashed #fb923c',
							cursor: interactive ? 'pointer' : 'default',
						}}
					/>
				</Tooltip>
			))}
		</Box>
		{interactive && (
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={closePopover}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				PaperProps={{ sx: { p: 2, width: 360 } }}
			>
				<Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
					Map "<i>{activeRaw}</i>" to canonical brand
				</Typography>
				<Autocomplete
					size="small"
					options={cat}
					value={pickedBrand}
					getOptionLabel={(o) => o?.name || ''}
					isOptionEqualToValue={(a, b) => a.id_brand === b.id_brand}
					onChange={(_, v) => setPickedBrand(v)}
					filterOptions={(opts, { inputValue }) => {
						const q = (inputValue || '').toLowerCase()
						if (!q) return opts.slice(0, 50)
						return opts.filter(o => (o.nameLower || (o.name || '').toLowerCase()).includes(q)).slice(0, 50)
					}}
					renderInput={(params) =>
						<TextField {...params} placeholder="Search canonical brand…" autoFocus />
					}
				/>
				<Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
					<Button size="small" onClick={closePopover} disabled={busy}>Cancel</Button>
					<Button size="small" variant="contained"
						disabled={!pickedBrand || busy}
						onClick={confirmMapping}
					>
						{busy ? 'Saving…' : 'Map & save'}
					</Button>
				</Stack>
				<Divider sx={{ my: 1.5 }}>or</Divider>
				<Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
					<Button size="small" variant="outlined"
						disabled={busy}
						onClick={() => setCreateOpen(true)}
					>
						Create new brand "{activeRaw}"
					</Button>
				</Stack>
			</Popover>
		)}
		{interactive && createOpen && (
			<BrandCreateDialog
				open={createOpen}
				initialName={activeRaw}
				onClose={() => setCreateOpen(false)}
				onCreated={handleCreated}
			/>
		)}
	</>
}


// Modal that creates a brand in the Puig catalog and returns the new
// {id_brand, name} via `onCreated`. Used from UnmatchedBrandChips when
// the operator hits "Create new brand" instead of mapping to existing.
// The dialog talks to VT.back (/admin/brands/{companies,create}); VT.back
// proxies the call to the Puig deploy and invalidates its 24h server-side
// brand cache so the new brand is visible immediately to subsequent
// /api/vt/brands fetches.
export function BrandCreateDialog({ open, initialName, onClose, onCreated }) {
	const [name, setName] = useState(initialName || '')
	const [displayName, setDisplayName] = useState(initialName || '')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)

	// Reset on open so reopening with a different raw string starts fresh.
	useEffect(() => {
		if (!open) return
		setName(initialName || '')
		setDisplayName(initialName || '')
		setError(null)
	}, [open, initialName])

	const submit = async () => {
		setError(null)
		const trimmedName = (name || '').trim()
		if (!trimmedName) { setError('Brand name is required.'); return }
		setBusy(true)
		// id_company is intentionally omitted — VT.back resolves the catalog's
		// "Unassigned Company" id and forwards it, matching what the Puig
		// admin UI does when an operator skips the dropdown. Reassign from
		// the Puig admin later if needed.
		const r = await api.post(`${BACKEND_APIS.VT}/admin/brands/create`, {
			name: trimmedName,
			display_name: (displayName || trimmedName).trim(),
			validated: true,
		})
		setBusy(false)
		if (!r.ok) {
			setError(r.error || 'Create failed.')
			return
		}
		const created = r.data || {}
		const newId = created.id_brand || created.brandID
		const newName = created.name || created.brandName || trimmedName
		if (!newId) {
			setError('Backend accepted the create but did not return an id.')
			return
		}
		// Append to the in-memory catalogue so the autocomplete on the
		// caller (and any other open editor) sees the new brand right away.
		_appendBrandToCatalogue({ id_brand: newId, name: newName })
		onCreated({ id_brand: newId, name: newName })
	}

	return (
		<Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
			<DialogTitle>Create brand</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					{error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
					<TextField
						label="Brand name" size="small" autoFocus
						value={name}
						onChange={(e) => setName(e.target.value)}
						helperText="Canonical name stored in the Puig catalog."
					/>
					<TextField
						label="Display name (optional)" size="small"
						value={displayName}
						onChange={(e) => setDisplayName(e.target.value)}
						placeholder={name || 'Same as brand name'}
						helperText="What we show in UIs. Defaults to the brand name."
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={busy}>Cancel</Button>
				<Button variant="contained" onClick={submit} disabled={busy || !name.trim()}>
					{busy ? 'Creating…' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}


// "Needs QA" badge. Replaces the old confidence display: we no longer
// surface a 0..1 reliability number, just a binary "human, please look
// at this one" flag set by the AI itself or by the brand-normalization
// pipeline (when there are unmatched_brands). Tooltip shows the
// AI-provided reason + model + prompt version.
function NeedsQABadge({ aiReview }) {
	if (!aiReview) return null
	const needsQa = !!aiReview.needs_qa
	if (!needsQa) return null
	const reason = aiReview.qa_reason || (
		(aiReview.unmatched_brands && aiReview.unmatched_brands.length)
			? `unmatched brand(s): ${aiReview.unmatched_brands.join(', ')}`
			: 'AI flagged this for human review'
	)
	return <Tooltip title={
		<Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
			<Box sx={{ fontWeight: 700, mb: 0.5 }}>Needs QA</Box>
			<Box>{reason}</Box>
			{aiReview.model && (
				<Box sx={{ mt: 0.75, opacity: 0.7, fontSize: 10 }}>
					{aiReview.model}{aiReview.prompt_version ? ` · ${aiReview.prompt_version}` : ''}
				</Box>
			)}
		</Box>
	}>
		<Chip label="NEEDS QA" size="small" sx={{
			fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
			bgcolor: '#fee2e2', color: '#991b1b',
			border: `1.5px solid ${NEEDS_QA_COLOR}`,
			height: 22,
			'& .MuiChip-label': { px: 1 },
		}} />
	</Tooltip>
}


// Big visibility callout — the metric the QA cares about most. Shown as a
// large percentage in the score's colour, with the viewport counter as a
// secondary line.
// Auto-vs-manual badge for sliders / product-sliders. Auto means the
// rotation runs by itself → visibility is split evenly across slides;
// manual means the user has to advance, so slide 1 keeps 100% and the
// rest decay 10% per position.
function AutoModeBadge({ auto }) {
	if (auto) {
		return <Chip label="AUTO ROTATE" size="small" sx={{
			fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
			bgcolor: '#e0f2fe', color: '#0369a1',
			border: '1.5px solid #7dd3fc',
			height: 22,
		}} />
	}
	return <Chip label="MANUAL" size="small" sx={{
		fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
		bgcolor: '#f3f4f6', color: '#4b5563',
		border: '1.5px solid #d1d5db',
		height: 22,
	}} />
}


function VisibilityBadge({ score, viewport, isContainer, isAuto }) {
	const color = visibilityColor(score || 0)
	const subLabel = isContainer
		? (isAuto ? 'Visibility (auto, split)' : 'Visibility (manual decay)')
		: 'Visibility'
	return <Box sx={{
		flex: '0 0 auto',
		minWidth: 130,
		textAlign: 'center',
		padding: '8px 12px',
		borderRadius: 2,
		border: `2px solid ${color}`,
		bgcolor: `${color}11`,
	}}>
		<Box sx={{
			fontSize: 28, fontWeight: 800,
			color, lineHeight: 1, letterSpacing: '-0.02em',
		}}>
			{score != null ? `${score.toFixed(1)}%` : '—'}
		</Box>
		<Box sx={{ fontSize: 9, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.5 }}>
			{subLabel}
		</Box>
		{viewport != null && (
			<Box sx={{ fontSize: 10, color: 'text.disabled', mt: 0.25 }}>
				viewport {viewport}
			</Box>
		)}
	</Box>
}


function SectionLabel({ children }) {
	return <Box sx={{
		fontSize: 10, fontWeight: 700,
		textTransform: 'uppercase', letterSpacing: '0.08em',
		color: 'text.secondary', mb: 0.5,
	}}>{children}</Box>
}


// Brand chip with deterministic colour — same brand always renders with the
// same hue, so they're recognisable across cards.
function BrandChip({ brand }) {
	const name = brand?.name || brand
	if (!name) return null
	const hash = String(name).split('').reduce((h, c) => c.charCodeAt(0) + ((h << 5) - h), 0)
	const hue = Math.abs(hash) % 360
	return <Chip
		label={name}
		sx={{
			fontSize: 13, fontWeight: 700,
			bgcolor: `hsl(${hue}, 70%, 92%)`,
			color: `hsl(${hue}, 65%, 30%)`,
			border: `1.5px solid hsl(${hue}, 60%, 70%)`,
			height: 28,
		}}
	/>
}


// Category chip — fixed palette (4 closed values).
const CATEGORY_PALETTE = {
	fragrances: { bg: '#fce4ec', fg: '#ad1457', border: '#f48fb1' },
	makeup:     { bg: '#ede7f6', fg: '#4527a0', border: '#9575cd' },
	cosmetic:   { bg: '#e0f7fa', fg: '#00695c', border: '#4db6ac' },
	other:      { bg: '#fff3e0', fg: '#e65100', border: '#ffb74d' },
}
function CategoryChip({ category, size = 'md' }) {
	if (!category) return null
	const c = CATEGORY_PALETTE[category] || { bg: '#eef0f4', fg: '#444', border: '#bbb' }
	const big = size === 'md'
	return <Chip
		label={category}
		sx={{
			fontSize: big ? 13 : 11, fontWeight: 700,
			bgcolor: c.bg, color: c.fg,
			border: `1.5px solid ${c.border}`,
			height: big ? 28 : 22,
			textTransform: 'uppercase',
			letterSpacing: '0.04em',
		}}
	/>
}


// Collects all brands across an array of sub-items, deduped by id_brand or
// name (whichever is present). Used by the slider header to summarise.
function aggregateBrands(subs) {
	const seen = new Map()
	for (const s of subs) {
		const list = s.brands || (s.brand ? [s.brand] : [])
		for (const b of list) {
			const key = b?.id_brand != null ? `id:${b.id_brand}` : `n:${b?.name || b}`
			if (!seen.has(key)) seen.set(key, b)
		}
	}
	return Array.from(seen.values())
}


// Collects all categories across sub-items, deduped, preserving CATEGORY_PALETTE
// order so colour layout stays consistent.
function aggregateCategories(subs) {
	const seen = new Set()
	for (const s of subs) {
		for (const c of (s.categories || [])) seen.add(c)
	}
	const order = Object.keys(CATEGORY_PALETTE)
	return Array.from(seen).sort((a, b) => order.indexOf(a) - order.indexOf(b))
}


function CatsLine({ cats }) {
	return <Box sx={{ fontSize: 14, mb: 0.5 }}>
		<strong style={{ color: '#57606a' }}>Categories:</strong>{' '}
		{cats.map(c => (
			<Chip key={c} label={c} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: 11 }} />
		))}
	</Box>
}

function BrandsLine({ brands }) {
	return <Box sx={{ fontSize: 14, mb: 0.5 }}>
		<strong style={{ color: '#57606a' }}>Brands:</strong>{' '}
		{brands.map((b, i) => {
			const name = b.name || b
			return name ? (
				<Chip key={i} label={name} size="small"
					sx={{ mr: 0.5, mb: 0.5, fontSize: 11, bgcolor: '#eef2ff', color: '#2563eb' }} />
			) : null
		})}
	</Box>
}

function SubItemRow({ sub, index, subKey, childLabel, group, subIdx, parentKind, onClick, rowRef }) {
	const score = sub.visibility_score
	const color = visibilityColor(score || 0)
	const subtype = sub.type
	const cats = sub.categories || []
	const brands = sub.brands || (sub.brand ? [sub.brand] : [])
	const eyecatch = asStringList(sub.eyecatch)
	const product = sub.product || ''
	const sponsored = !!sub.sponsored
	// 869ehb2zd — only a HISTORICAL product-slider slide carries an array of
	// products. A post-cutover product (and a product-list fragment) uses the
	// flat product/eyecatch/sponsored fields, because it IS one product.
	const isPairCrop = (sub.leaf_kind || (parentKind === 'product-slider'
		&& subKey === 'slides' ? 'product-slide' : '')) === 'product-slide'
	const products = isPairCrop ? (sub.products || []) : null
	const onCropClick = (e) => {
		e.stopPropagation()
		// Open the lightbox showing every crop in the group, starting at this
		// sub-item — the QA can paginate forward/backward to compare.
		if (group) openGroupLightbox(group, subIdx ?? 0)
		else lightboxSrc.value = sub.crop
	}
	const subNeedsQa = !!sub.ai_review?.needs_qa
	return <Box
		ref={rowRef}
		onClick={onClick ? (e) => { e.stopPropagation(); onClick() } : undefined}
		sx={{
			display: 'flex', gap: 1.5, alignItems: 'flex-start',
			bgcolor: subNeedsQa ? '#fff5f5' : '#fff',
			borderRadius: 1, p: 1.25,
			border: subNeedsQa ? `2px solid ${NEEDS_QA_COLOR}` : '1px solid #e0e6ee',
			boxShadow: subNeedsQa ? `0 0 0 3px ${NEEDS_QA_COLOR}22` : 'none',
			cursor: onClick ? 'pointer' : 'default',
		}}
	>
		{sub.crop ? (
			<img src={sub.crop} alt=""
				onClick={onCropClick}
				style={{
					width: 150, height: 150, objectFit: 'contain',
					borderRadius: 4, border: `2px solid ${color}`,
					background: '#f8f9fb',
					flex: '0 0 auto',
					cursor: 'zoom-in',
				}} />
		) : (
			<Box sx={{ width: 150, height: 150, bgcolor: 'grey.200', borderRadius: 1, flex: '0 0 auto' }} />
		)}
		<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
			{/* Header row: child label + subtype as title, big % on the right */}
			<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box sx={{
						fontSize: 14, fontWeight: 700,
						color: '#0c1a3e', textTransform: 'uppercase',
						letterSpacing: '0.02em',
					}}>
						{subtype || childLabel}
					</Box>
					<Box sx={{ fontSize: 11, color: 'text.secondary' }}>
						{childLabel} {index}
					</Box>
				</Box>
				<Box sx={{
					textAlign: 'center',
					padding: '4px 10px',
					borderRadius: 1.5,
					border: `2px solid ${color}`,
					bgcolor: `${color}11`,
					minWidth: 70,
				}}>
					<Box sx={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1 }}>
						{score != null ? `${score.toFixed(1)}%` : '—'}
					</Box>
					<Box sx={{ fontSize: 8, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.25 }}>
						visibility
					</Box>
				</Box>
				<NeedsQABadge aiReview={sub.ai_review} />
			</Box>

			{/* Single-product (product-list fragment): show flat name. */}
			{!products && product && (
				<Box sx={{ fontSize: 12, fontWeight: 600, color: '#0c1a3e' }}>
					{product}
				</Box>
			)}

			{brands.length > 0 && (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
					<SectionLabel>Brands</SectionLabel>
					{brands.map((b, i) => <BrandChip key={i} brand={b} />)}
				</Box>
			)}

			<UnmatchedBrandChips aiReview={sub.ai_review} />

			{cats.length > 0 && (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
					<SectionLabel>Categories</SectionLabel>
					{cats.map(c => <CategoryChip key={c} category={c} size="sm" />)}
				</Box>
			)}

			{/* Single-product flat eyecatch / sponsored row. */}
			{!products && (eyecatch.length > 0 || sponsored) && (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
					{sponsored && (
						<Chip label="SPONSORED" size="small"
							sx={{
								bgcolor: '#1f2937', color: '#fff',
								fontWeight: 800, fontSize: 9, letterSpacing: '0.08em', height: 20,
							}}
						/>
					)}
					{eyecatch.map((e, i) => (
						<Chip key={i} label={e} size="small"
							sx={{ bgcolor: '#fef3c7', color: '#854d0e', fontWeight: 600, height: 20 }}
						/>
					))}
				</Box>
			)}

			{/* Multi-product (product-slider slide): list of cards. */}
			{products && products.length > 0 && (
				<Box sx={{ mt: 0.5 }}>
					<SectionLabel>Products in this slide ({products.length})</SectionLabel>
					<Stack spacing={0.75} sx={{ mt: 0.5 }}>
						{products.map((p, i) => (
							<Box key={p.id || i} sx={{
								p: 0.75, borderRadius: 1,
								bgcolor: '#fafbfc', border: '1px solid #e0e6ee',
							}}>
								<Box sx={{ fontSize: 12, fontWeight: 600, color: '#0c1a3e' }}>
									{p.name || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>}
								</Box>
								<Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
									{(p.brands || []).map((b, j) => <BrandChip key={j} brand={b} />)}
									{p.sponsored && (
										<Chip label="SPONSORED" size="small"
											sx={{
												bgcolor: '#1f2937', color: '#fff',
												fontWeight: 800, fontSize: 9, letterSpacing: '0.08em', height: 18,
											}}
										/>
									)}
									{asStringList(p.eyecatch).map((e, j) => (
										<Chip key={`e${j}`} label={e} size="small"
											sx={{ bgcolor: '#fef3c7', color: '#854d0e', fontWeight: 600, height: 18 }}
										/>
									))}
								</Stack>
							</Box>
						))}
					</Stack>
				</Box>
			)}

			{sub.destination_url && (
				<Box sx={{ fontSize: 10, color: 'text.disabled', wordBreak: 'break-all' }}>
					→ {sub.destination_url}
				</Box>
			)}
		</Box>
	</Box>
}


// Legacy (kept for reference, no longer used)
const ItemCard = React.forwardRef(function ItemCard({ item, idx, focused, onClick }, ref) {
	const score = item.visibility_score
	const color = visibilityColor(score || 0)
	const subtype = item.type || item._parent?.type
	const cats = item.categories || []
	const brands = item.brands || (item.brand ? [item.brand] : [])
	return <Paper ref={ref}
		variant="outlined"
		onClick={onClick}
		sx={{
			p: 2, display: 'flex', gap: 2, cursor: 'pointer',
			borderColor: focused ? color : undefined,
			borderWidth: focused ? 2 : 1,
			boxShadow: focused ? `0 0 0 3px ${color}33` : 'none',
		}}
	>
		{item.crop ? (
			<img src={item.crop} alt="" style={{
				width: 240, height: 240, objectFit: 'contain',
				borderRadius: 6, border: `2px solid ${color}`,
				background: '#f8f9fb',
				flex: '0 0 auto',
			}} />
		) : (
			<Box sx={{ width: 240, height: 240, bgcolor: 'grey.200', borderRadius: 1, flex: '0 0 auto' }} />
		)}
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
				<Chip label={item._label} sx={{ fontSize: 13, fontWeight: 600 }} />
				{subtype && <Chip label={subtype} variant="outlined" sx={{ fontSize: 12 }} />}
				<Chip
					label={`visibility ${score != null ? score.toFixed(1) : '?'}`}
					sx={{ fontSize: 13, bgcolor: color, color: '#fff', fontWeight: 700 }}
				/>
				{item.viewport_counter != null && (
					<Chip label={`viewport ${item.viewport_counter}`} variant="outlined"
						sx={{ fontSize: 12 }} />
				)}
			</Stack>
			{cats.length > 0 && (
				<Box sx={{ fontSize: 14, mb: 0.5 }}>
					<strong style={{ color: '#57606a' }}>Categories:</strong>{' '}
					{cats.map(c => (
						<Chip key={c} label={c} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: 11 }} />
					))}
				</Box>
			)}
			{brands.length > 0 && (
				<Box sx={{ fontSize: 14, mb: 0.5 }}>
					<strong style={{ color: '#57606a' }}>Brands:</strong>{' '}
					{brands.map((b, i) => {
						const name = b.name || b
						return name ? (
							<Chip key={i} label={name} size="small"
								sx={{ mr: 0.5, mb: 0.5, fontSize: 11, bgcolor: '#eef2ff', color: '#2563eb' }} />
						) : null
					})}
				</Box>
			)}
			{item.bbox_page && (
				<Box sx={{ fontSize: 12, color: 'text.secondary', mt: 1, fontFamily: 'monospace' }}>
					bbox: x={Math.round(item.bbox_page.x)} y={Math.round(item.bbox_page.y)}
					{' · '}{Math.round(item.bbox_page.width)}×{Math.round(item.bbox_page.height)}
				</Box>
			)}
			{item.destination_url && (
				<Box sx={{ fontSize: 12, color: 'text.disabled', wordBreak: 'break-all', mt: 0.5 }}>
					→ {item.destination_url}
				</Box>
			)}
			{item.notes && (
				<Box sx={{ fontSize: 13, color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
					“{item.notes}”
				</Box>
			)}
		</Box>
	</Paper>
})
